import {useCallback, useEffect, useState} from 'react'
import {type PDFDocumentProxy} from 'pdfjs-dist'

import {logger} from '#/logger'
import {
  acquirePdfDocument,
  getPdfPageRatio,
  type PdfSource,
  pdfSourceKey,
  releasePdfDocument,
} from './pdf.web'

export type PdfDocumentStatus = 'idle' | 'loading' | 'ready' | 'error'

export type UsePdfDocumentResult = {
  status: PdfDocumentStatus
  doc: PDFDocumentProxy | null
  docKey: string
  numPages: number
  /** width / height of the first page */
  ratio: number
  isPdf: boolean
  retry: () => void
}

const DEFAULT_RATIO = 1 / 1.414 // A4 portrait

/**
 * Loads a document once and holds a lease on it while mounted. Loading only
 * starts when `enabled` is true, so off-screen posts stay unloaded.
 */
export function usePdfDocument({
  source,
  enabled,
}: {
  source: PdfSource
  enabled: boolean
}): UsePdfDocumentResult {
  const [status, setStatus] = useState<PdfDocumentStatus>(
    enabled ? 'loading' : 'idle',
  )
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(1)
  const [ratio, setRatio] = useState(DEFAULT_RATIO)
  const [attempt, setAttempt] = useState(0)

  const key = pdfSourceKey(source)

  useEffect(() => {
    if (!enabled) return

    const controller = new AbortController()
    let cancelled = false
    let acquired = false

    setStatus('loading')
    setDoc(null)
    ;(async () => {
      try {
        const loaded = await acquirePdfDocument(source, controller.signal)
        acquired = true
        if (cancelled) return
        const pageRatio = await getPdfPageRatio(loaded, 1)
        if (cancelled) return
        setDoc(loaded)
        setNumPages(loaded.numPages)
        setRatio(pageRatio)
        setStatus('ready')
      } catch (e) {
        if (cancelled) return
        logger.debug('Failed to load document', {docKey: key, safeMessage: e})
        setStatus('error')
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
      if (acquired) {
        releasePdfDocument(source)
      }
    }
    // `source` is memoised by both callers (`PdfCarousel`, `PdfFullscreen`), so
    // it re-runs exactly when the document changes - which is what `key` was
    // standing in for when it was left out of this list.
  }, [source, key, enabled, attempt])

  const retry = useCallback(() => {
    setAttempt(value => value + 1)
  }, [])

  return {
    status,
    doc,
    docKey: key,
    numPages,
    ratio,
    isPdf: source.kind === 'remote' || source.kind === 'bytes',
    retry,
  }
}
